import { useState, useEffect } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  getDesembolsosPlatam,
  type TDesembolsosPlatamResponse,
  type TDesembolsoCutoff,
  type TDesembolsoOrder,
} from "@/api/customs/textiles";
import { PURCHASES } from "@/config/resources";

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercentage = (value: number) => {
  return `${(value * 100).toFixed(2)}%`;
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-CO", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatShortDate = (dateStr: string) => {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

interface OrderRowProps {
  order: TDesembolsoOrder;
}

const OrderRow = ({ order }: OrderRowProps) => {
  return (
    <TableRow hover>
      <TableCell>
        <MuiLink
          href={`/${PURCHASES}/${order.purchase_id}/show`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 500 }}
        >
          {order.purchase_number}
        </MuiLink>
      </TableCell>
      <TableCell align="center">{order.payment_id || "-"}</TableCell>
      <TableCell>{order.client || "-"}</TableCell>
      <TableCell align="center">
        <Tooltip title={`Fecha de entrega`}>
          <span>{formatShortDate(order.delivered_at)}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          label={`${order.days_delay}d`}
          color="default"
          variant="outlined"
        />
      </TableCell>
      <TableCell align="right">{formatCurrency(order.final_amount)}</TableCell>
      <TableCell align="right">
        <Typography color="error.main" variant="body2">
          -{formatCurrency(order.discount)}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          ({formatPercentage(order.discount_rate)})
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography fontWeight="bold" color="success.main">
          {formatCurrency(order.disbursement)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

interface CutoffCardProps {
  cutoff: TDesembolsoCutoff;
  defaultExpanded?: boolean;
}

const CutoffCard = ({ cutoff, defaultExpanded = false }: CutoffCardProps) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isPast = cutoff.is_past;
  const hasOrders = cutoff.orders.length > 0;

  return (
    <Card
      sx={{
        mb: 2,
        border: isPast ? "1px solid" : "2px solid",
        borderColor: isPast ? "grey.400" : "primary.main",
        bgcolor: "background.paper",
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Fecha y estado */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarTodayIcon
              color={isPast ? "disabled" : "primary"}
              fontSize="small"
            />
            <Box>
              <Typography variant="h6" component="div">
                {formatDate(cutoff.cutoff_date)}
              </Typography>
              <Chip
                size="small"
                label={isPast ? "Pasado" : "Próximo"}
                color={isPast ? "default" : "primary"}
                variant={isPast ? "outlined" : "filled"}
              />
            </Box>
          </Box>

          {/* Totales */}
          <Stack direction="row" spacing={3}>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {translate("resources.textiles.fields.descuento")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TrendingDownIcon fontSize="small" color="error" />
                <Typography variant="h6" color="error.main">
                  {formatCurrency(cutoff.total_discount)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {translate("resources.textiles.desembolsos.total_desembolso")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TrendingUpIcon fontSize="small" color="success" />
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {formatCurrency(cutoff.total_disbursement)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Toggle para ver pedidos */}
        {hasOrders && (
          <Box sx={{ mt: 2 }}>
            <Box
              onClick={() => setExpanded(!expanded)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": { color: "primary.main" },
              }}
            >
              <IconButton size="small">
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {cutoff.orders.length} pedido{cutoff.orders.length !== 1 && "s"}
              </Typography>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.800" }}>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Pedido
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          ID Platam
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Cliente
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Entregado
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Plazo
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Monto
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Descuento
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold" color="common.white">
                          Desembolso
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cutoff.orders.map((order, index) => (
                      <OrderRow key={index} order={order} />
                    ))}
                    {/* Fila de totales */}
                    <TableRow sx={{ bgcolor: "grey.800" }}>
                      <TableCell colSpan={5}>
                        <Typography variant="subtitle2" fontWeight="bold" color="common.white">
                          TOTAL
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold" color="common.white">
                          {formatCurrency(
                            cutoff.orders.reduce(
                              (sum, o) => sum + o.final_amount,
                              0
                            )
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="error.light"
                        >
                          -{formatCurrency(cutoff.total_discount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="success.light"
                        >
                          {formatCurrency(cutoff.total_disbursement)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        )}

        {!hasOrders && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              No hay pedidos en este corte
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const DesembolsosPlatamTable = () => {
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TDesembolsosPlatamResponse | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDesembolsosPlatam();
      setData(response);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.cutoffs.length === 0) {
    return (
      <Alert severity="info">
        No hay datos de desembolsos disponibles
      </Alert>
    );
  }

  // Separar pasados y futuros
  const pastCutoffs = data.cutoffs.filter((c) => c.is_past);
  const futureCutoffs = data.cutoffs.filter((c) => !c.is_past);

  // Calcular totales
  const totalFutureDisbursement = futureCutoffs.reduce(
    (sum, c) => sum + c.total_disbursement,
    0
  );
  const totalFutureDiscount = futureCutoffs.reduce(
    (sum, c) => sum + c.total_discount,
    0
  );

  return (
    <Box>
      {/* Resumen de próximos desembolsos */}
      <Card sx={{ mb: 3, bgcolor: "background.paper", border: "2px solid", borderColor: "primary.main" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary.main">
            📊 Resumen Próximos Desembolsos
          </Typography>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total a desembolsar
              </Typography>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {formatCurrency(totalFutureDisbursement)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total descuentos
              </Typography>
              <Typography variant="h4" color="error.main">
                {formatCurrency(totalFutureDiscount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Pedidos pendientes
              </Typography>
              <Typography variant="h4" color="text.primary">
                {futureCutoffs.reduce((sum, c) => sum + c.orders.length, 0)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Próximos cortes */}
      {futureCutoffs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            📅 Próximos Cortes
          </Typography>
          {futureCutoffs.map((cutoff, index) => (
            <CutoffCard
              key={cutoff.cutoff_date}
              cutoff={cutoff}
              defaultExpanded={index === 0}
            />
          ))}
        </Box>
      )}

      {/* Cortes pasados */}
      {pastCutoffs.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
            📜 Cortes Pasados
          </Typography>
          {pastCutoffs.map((cutoff) => (
            <CutoffCard
              key={cutoff.cutoff_date}
              cutoff={cutoff}
              defaultExpanded={false}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
